import React from 'react';
import WarningIcon from '@mui/icons-material/WarningAmberRounded';
import { Link } from 'react-router-dom';
import {
  Alert,
  AlertColor,
  Button,
  Collapse,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { important } from '../../../utils/global/helper';

import './styles.css';

export interface ColoredAlertProps {
  type: AlertColor;
  title?: string;
  subtitle: string;
  dismissible?: boolean;
  animate?: boolean;
  hideIcon?: boolean;
  containerSx?: SxProps<Theme>;
  persistentDismissAlertKey?: string;
  persistentDismissStorage?: Storage;
  button?: {
    text: string;
    onClick?: () => void;
    href?: string;
  };
}

function ColoredAlert({
  type,
  title,
  subtitle,
  dismissible = true,
  animate = true,
  hideIcon = false,
  containerSx,
  persistentDismissAlertKey,
  persistentDismissStorage = sessionStorage,
  button,
}: ColoredAlertProps) {
  const theme = useTheme();

  const [hidden, setHidden] = React.useState(
    !!persistentDismissAlertKey &&
      persistentDismissStorage.getItem(persistentDismissAlertKey) === 'true',
  );

  const handleClose = () => {
    if (!dismissible) return;
    setHidden(true);

    if (!persistentDismissAlertKey) return;
    persistentDismissStorage.setItem(persistentDismissAlertKey, 'true');
  };

  if (hidden && !animate) return null;

  return (
    <Collapse in={!hidden} sx={containerSx}>
      <Alert
        variant="standard"
        className="colored-alert"
        severity={type}
        onClose={dismissible ? handleClose : undefined}
        icon={hideIcon ? false : undefined}
        // only override icon if it's a warning - more rounded icon
        iconMapping={{
          warning: <WarningIcon />,
        }}>
        {title && <Typography variant="h3">{title}</Typography>}
        <Typography
          sx={{
            mt: title ? undefined : important(0),
            whiteSpace: 'pre-line',
          }}
          variant="body2">
          {subtitle}
        </Typography>
        {button && (
          // @ts-expect-error - Button component is not typed correctly
          <Button
            variant="link"
            onClick={button.onClick}
            LinkComponent={button.href ? Link : undefined}
            to={button.href}
            sx={{
              fontSize: theme.typography.body2.fontSize,
            }}>
            {button.text}
          </Button>
        )}
      </Alert>
    </Collapse>
  );
}

export default ColoredAlert;
